import { OB_EXPIRY_THRESHOLD_DAYS } from '@helpers/OnlineAccounts/getOpenBankingStatus';
import { hasExtendableProvider } from '@helpers/OnlineAccounts/hasExtendableProvider';
import { fetchGetOnlineAccount } from '@iwoca/lapi-client/edge';

import { findBankByName, OpenBankingBank } from '../getLinkedAccounts';

type OnlineAccountsResponse = Awaited<ReturnType<typeof fetchGetOnlineAccount>>;
type OnlineAccount = OnlineAccountsResponse['data'][number];

export type ReconsentConnection = {
    onlineAccountId: string;
    bankName?: string;
    logoUrl?: string;
    isExtendable: boolean;
};

const RECONSENTABLE_STATUSES = ['active', 'expired'];

const isNearExpiry = (expiryAt: string | undefined, now: number) => {
    if (!expiryAt) return false;

    const thresholdMs = OB_EXPIRY_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

    return new Date(expiryAt).getTime() - now <= thresholdMs;
};

const isExtendable = (entry: OnlineAccount, now: number) => {
    if (!hasExtendableProvider(entry)) return false;

    if (entry.status?.code === 'expired') return true;

    return (
        entry.status?.code === 'active' && isNearExpiry(entry.expiry_at, now)
    );
};

export const getConnectionsNeedingRenewal = (
    connections: ReconsentConnection[],
    renewedConnectionIds: string[]
) =>
    connections.filter(
        (connection) =>
            connection.isExtendable &&
            !renewedConnectionIds.includes(connection.onlineAccountId)
    );

export const getReconsentConnections = (
    response: OnlineAccountsResponse | undefined,
    banks: OpenBankingBank[] = [],
    now: number = Date.now()
): ReconsentConnection[] => {
    if (!response?.data) return [];

    const connectionsById = response.data
        .filter((entry) => RECONSENTABLE_STATUSES.includes(entry.status?.code))
        .map((entry) => {
            const bank = findBankByName(entry.provider, banks);

            return [
                entry.online_account_id,
                {
                    onlineAccountId: entry.online_account_id,
                    bankName: entry.provider ?? undefined,
                    logoUrl: bank?.logo_url,
                    isExtendable: isExtendable(entry, now)
                }
            ] as const;
        });

    return Array.from(new Map(connectionsById).values());
};
